import Handlebars from "handlebars";
import { useEffect } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { Tabs } from "../../components/Tabs";
import { EmailTemplate, EmailTemplateList } from "../../models/notification";
import { UserInfo } from "../../models/user-info";
import notificationService from "../../services/notification-service";
import AcademicEmailComposerFormStep1 from "./AcademicEmailComposerForm.Step1";
import AcademicEmailComposerFormStep2 from "./AcademicEmailComposerForm.Step2";
import AcademicEmailComposerFormStep3 from "./AcademicEmailComposerForm.Step3";
import {
  AcademicEmailComposerFormValues,
  UnitOfStudy,
} from "./AcademicEmailComposerForm.Types";

interface AcademicEmailComposerProps {
  setLoading: (loading: boolean) => void;
  setShowThankYou: (showThankYou: boolean) => void;
  userInfo: UserInfo;
  setEmailTemplates: (templates: EmailTemplateList) => void;
  unitsOfStudy: UnitOfStudy[];
  setEmailTemplate: (template?: EmailTemplate) => void;
  setValue: any;
  formValues: AcademicEmailComposerFormValues;
  setCurrentStep: (step: number) => void;
  currentStep: number;
  handleSubmit: any;
  onSubmit: () => void;
  control: any;
  selectedFaculty: string;
  emailTemplates: EmailTemplateList;
  emailTemplate: EmailTemplate;
  getEmailTemplateVariables: (values: AcademicEmailComposerFormValues) => void;
}

const AcademicEmailComposerForm = ({
  setLoading,
  setEmailTemplates,
  unitsOfStudy,
  setEmailTemplate,
  setValue,
  formValues,
  setCurrentStep,
  currentStep,
  handleSubmit,
  onSubmit,
  control,
  selectedFaculty,
  emailTemplates,
  emailTemplate,
  getEmailTemplateVariables,
}: AcademicEmailComposerProps) => {
  const navigate = useNavigate();

  // fetch email templates
  useEffect(() => {
    const fetchEmailTemplates = async () => {
      setLoading(true);
      notificationService
        .getEmailTemplates()
        .then((templates) => {
          setEmailTemplates(templates);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    fetchEmailTemplates();
  }, [setEmailTemplates, setLoading]);

  const compileSubject = (subject: string, unitOfStudyCode: string) => {
    if (!subject) return "";
    const compliedSubject = Handlebars.compile(subject)({
      UnitOfStudyCode: unitOfStudyCode,
      UnitOfStudyName: unitsOfStudy?.find(
        (unit) => unit.value === unitOfStudyCode,
      )?.label,
    });
    setValue("subject", compliedSubject);
  };

  const getEmailTemplate = (name: string) => {
    setLoading(true);
    notificationService
      .getEmailTemplate(name)
      .then((template) => {
        setEmailTemplate(template);
        compileSubject(template?.subject, formValues.unitOfStudy);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangeEmailTemplate = (template: string) => {
    if (template) {
      getEmailTemplate(template);
    } else {
      setEmailTemplate(undefined);
    }
  };

  const goBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleTabClick = (tab: string) => {
    if (tab === "Status") {
      navigate("/academic-email-status");
    }
  };

  return (
    <div className="flex flex-col pt-8 px-10 pb-20 relative">
      {/* Header */}
      <div className="absolute right-0 mt-4 mr-8">
        <span className="text-[#00000040]">
          <AiOutlineCloseCircle
            className="w-8 h-8"
            onClick={() => navigate("/dashboard")}
            role="button"
            tabIndex={0}
          />
        </span>
      </div>
      <div className="mb-8 w-full">
        <Tabs
          items={[
            { title: "Compose an Email", active: true },
            { title: "Status" },
          ]}
          onClick={handleTabClick}
          className="mb-8"
        />

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 h-4 rounded-full relative">
          <div
            className={`
                ${currentStep === 1 ? "w-1/3" : ""}
                ${currentStep === 2 ? "w-2/3" : ""}
                ${currentStep === 3 ? "w-full" : ""}
                `}
          >
            <div
              className="bg-[#424242] h-4 rounded-full grid"
              style={{
                width:
                  currentStep === 1
                    ? "50%"
                    : currentStep === 2
                    ? "75%"
                    : "83.3%",
              }}
            >
              <div
                className="rounded-full bg-white w-3 h-3 justify-self-end"
                style={{
                  marginRight: "2px",
                  marginTop: "2px",
                }}
              ></div>
            </div>
          </div>

          <div className="flex justify-between text-sm mt-2">
            <span className="w-1/3 text-center">Choose Template</span>
            <span className="w-1/3 text-center">Content & Format</span>
            <span className="w-1/3 text-center">Preview</span>
          </div>
        </div>

        {/* Steps */}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="mt-8">
        {
          // Step 1
          currentStep === 1 && (
            <AcademicEmailComposerFormStep1
              control={control}
              formValues={formValues}
              unitsOfStudy={unitsOfStudy}
              selectedFaculty={selectedFaculty}
              emailTemplate={emailTemplate as EmailTemplate}
              compileSubject={compileSubject}
              emailTemplates={emailTemplates}
              handleChangeEmailTemplate={handleChangeEmailTemplate}
            />
          )
        }
        {
          // Step 2
          currentStep === 2 && (
            <AcademicEmailComposerFormStep2
              control={control}
              formValues={formValues}
              emailTemplate={emailTemplate as EmailTemplate}
              getEmailTemplateVariables={getEmailTemplateVariables}
              goBack={goBack}
            />
          )
        }
        {
          // Step 3
          currentStep === 3 && (
            <AcademicEmailComposerFormStep3
              control={control}
              formValues={formValues}
              emailTemplate={emailTemplate as EmailTemplate}
              getEmailTemplateVariables={getEmailTemplateVariables}
              goBack={goBack}
              setLoading={setLoading}
              selectedFaculty={selectedFaculty}
              unitsOfStudy={unitsOfStudy}
            />
          )
        }
      </form>
    </div>
  );
};

export default AcademicEmailComposerForm;
